import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { assessmentPairs, aolSteps } from "@/data";

export default function Assessment() {
  return (
    <div className="py-12 px-4">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-10">
          <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-2">Evaluation System</p>
          <h1 className="text-4xl font-bold text-foreground mb-3">Competency-Based Assessment</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            GBSRC's assessment philosophy is grounded in measuring actual capability — not memory. A rich, multi-modal architecture mirrors the complexity of professional practice.
          </p>
        </div>

        {/* CCE section */}
        <div className="bg-primary/5 border-l-4 border-primary rounded-r-xl p-6 mb-10">
          <h2 className="text-xl font-bold text-foreground mb-2">Continuous Comprehensive Evaluation (CCE)</h2>
          <p className="text-foreground/70 leading-relaxed">
            In place of high-stakes terminal examinations, GBSRC employs CCE — distributing assessment across the academic year to ensure sustained engagement and multidimensional learning measurement. No single examination determines a student's academic standing; growth is tracked continuously, enabling timely intervention and support.
          </p>
        </div>

        {/* Assessment pairs table */}
        <div className="mb-12">
          <h2 className="text-xl font-bold text-foreground mb-4">Assessment Methods &amp; Competencies Evaluated</h2>
          <div className="overflow-hidden border border-border rounded-xl">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-primary text-primary-foreground">
                  <th className="text-left p-4 font-semibold w-1/2">Assessment Method</th>
                  <th className="text-left p-4 font-semibold w-1/2">Competency Evaluated</th>
                </tr>
              </thead>
              <tbody>
                {assessmentPairs.map((row, i) => (
                  <motion.tr
                    key={row.method}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.04 }}
                    className={i % 2 === 0 ? "bg-background" : "bg-muted/30"}
                  >
                    <td className="p-4 border-b border-border text-foreground/80 font-medium">{row.method}</td>
                    <td className="p-4 border-b border-border text-foreground/70">{row.competency}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* AoL section */}
        <div>
          <div className="text-center mb-8">
            <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-2">Quality Assurance</p>
            <h2 className="text-2xl font-bold text-foreground">Assurance of Learning (AoL) — 6-Step Cycle</h2>
            <p className="text-muted-foreground mt-2 max-w-xl mx-auto text-sm">
              Aligned with AACSB Standards 4 &amp; 5 and the EFMD Quality Improvement System, GBSRC closes the quality loop between curriculum design, delivery, and graduate outcomes.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {aolSteps.map((s, i) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-background border border-border rounded-xl p-5 hover:border-primary/40 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl font-bold text-primary/20 font-mono">{s.step}</span>
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  </div>
                </div>
                <h3 className="font-bold text-foreground mb-2 text-sm">{s.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Loop indicator */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-3 bg-primary text-white px-6 py-3 rounded-full text-sm font-semibold">
              <span>Closing the Loop</span>
              <span className="w-2 h-2 rounded-full bg-white/60" />
              <span>Continuous Quality Improvement</span>
              <span className="w-2 h-2 rounded-full bg-white/60" />
              <span>Evidence-Based Decisions</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
